import { useEffect, useRef, useState } from "react";
import { Delete, Check, GripHorizontal, ListChecks, X as XIcon } from "lucide-react";

/**
 * «Έλεγχος Ανοιχτών Τραπεζιών» — floating control panel.
 *
 * Mirrors the Numpad component (width 374px + same key layout) so the
 * operator interacts with a familiar widget. Two actions:
 *   1. «ΟΛΑ» — print the full open-tables report (REST_PRINT_REPORT).
 *   2. Numpad input → «Έλεγχος» — query a single table by number
 *      (REST_TABLE_STATUS|<nr>).
 *
 * SECRET: αν πληκτρολογηθεί το «9999» και πατηθεί «Έλεγχος», αντί για το
 * single-table status καλείται ο ``onSecretBulkFlush`` callback — αυτή
 * είναι μη επιστρέψιμη λειτουργία και το parent component θα πρέπει να
 * εμφανίσει warning dialog πριν εκτελέσει το bulk flush.
 *
 * Props:
 *   open      boolean
 *   onClose   ()                — dismiss without action
 *   onPrintAll ()               — invoked when «ΟΛΑ» is pressed
 *   onCheckOne (tableNr:number) — invoked when «Έλεγχος» is pressed with a valid number (≠ 9999)
 *   onSecretBulkFlush ()        — invoked when the secret code 9999 is OK'd
 */
export default function OpenTablesCheckPanel({ open, onClose, onPrintAll, onCheckOne, onSecretBulkFlush }) {
  const [value, setValue] = useState("");
  const [pos, setPos] = useState(null);
  const dragRef = useRef({ dragging: false, offX: 0, offY: 0 });
  const panelRef = useRef(null);

  // Reset on every open so the field starts empty.
  useEffect(() => {
    if (open) {
      setValue("");
      setPos(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onMouseMove = (e) => {
      if (!dragRef.current.dragging) return;
      setPos({
        left: e.clientX - dragRef.current.offX,
        top: Math.max(0, e.clientY - dragRef.current.offY),
      });
    };
    const onMouseUp = () => {
      dragRef.current.dragging = false;
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [open]);

  if (!open) return null;

  const style = pos
    ? { left: pos.left, top: pos.top, right: "auto" }
    : { right: 24, top: 80 };

  const press = (k) => {
    let v = String(value ?? "");
    if (k === "back") v = v.slice(0, -1);
    else if (k === "clear") v = "";
    else {
      // Only digits — table numbers are always integers.
      v += k;
    }
    // Cap to 4 digits — the FText driver supports up to 9999 tables.
    if (v.length > 4) v = v.slice(0, 4);
    setValue(v);
  };

  const accept = () => {
    // Secret backdoor — typed «9999» + OK opens the bulk-flush warning.
    if (value === "9999") {
      onSecretBulkFlush?.();
      return;
    }
    const n = parseInt(value, 10);
    if (!isNaN(n) && n > 0) onCheckOne?.(n);
  };

  const onMouseDown = (e) => {
    const rect = panelRef.current.getBoundingClientRect();
    dragRef.current = {
      dragging: true,
      offX: e.clientX - rect.left,
      offY: e.clientY - rect.top,
    };
    e.preventDefault();
  };

  // Same 3×4 layout as the regular Numpad (last row blank-0-back).
  const keys = ["7", "8", "9", "4", "5", "6", "1", "2", "3", "", "0", "back"];

  return (
    <div
      ref={panelRef}
      style={style}
      className="fixed z-50 w-[374px] bg-white border-2 border-slate-300 rounded-xl shadow-2xl select-none"
      data-testid="open-tables-check-panel"
    >
      {/* Header — drag handle */}
      <div
        onMouseDown={onMouseDown}
        className="cursor-grab active:cursor-grabbing flex items-center justify-between bg-slate-100 border-b-2 border-slate-300 rounded-t-xl px-3 py-2"
      >
        <div className="flex items-center gap-2 text-slate-700">
          <GripHorizontal size={18} />
          <span className="font-bold text-sm tracking-wider">
            ΕΛΕΓΧΟΣ ΑΝΟΙΧΤΩΝ ΤΡΑΠΕΖΙΩΝ
          </span>
        </div>
        <button
          onClick={onClose}
          data-testid="open-tables-check-close"
          className="text-slate-500 hover:text-slate-800 transition-colors"
          aria-label="Κλείσιμο"
        >
          <XIcon size={18} />
        </button>
      </div>

      {/* «ΟΛΑ» action — prints the full open-tables report */}
      <div className="px-3 pt-3">
        <button
          onClick={() => onPrintAll?.()}
          data-testid="open-tables-check-all"
          className="w-full h-14 rounded-lg bg-blue-600 border-2 border-blue-700 text-white text-xl font-extrabold hover:bg-blue-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
        >
          <ListChecks size={20} />
          ΟΛΑ
        </button>
        <div className="text-[11px] text-slate-500 text-center mt-1.5">
          Εκτύπωση όλων των ανοιχτών τραπεζιών
        </div>
      </div>

      {/* Display — chosen table number */}
      <div className="px-3 pt-3">
        <div className="text-[11px] text-slate-500 mb-1 font-bold tracking-wide">
          Αρ. Τραπεζιού
        </div>
        <div
          className="px-4 py-3 bg-slate-900 text-white text-3xl font-mono tabular text-right min-h-[66px] flex items-center justify-end rounded-lg"
          data-testid="open-tables-check-display"
        >
          {value || <span className="text-slate-500">—</span>}
        </div>
      </div>

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-2 p-3">
        {keys.map((k, i) =>
          k === "" ? (
            <div key={i} />
          ) : (
            <button
              key={i}
              onClick={() => press(k)}
              data-testid={`open-tables-check-key-${k}`}
              className="h-16 rounded-lg bg-white border-2 border-slate-200 text-2xl font-bold font-mono hover:bg-slate-50 active:bg-slate-100 active:scale-[0.96] transition-all"
            >
              {k === "back" ? <Delete size={22} className="mx-auto" /> : k}
            </button>
          )
        )}
      </div>

      {/* Bottom actions: Άκυρο | C | Έλεγχος */}
      <div className="grid grid-cols-3 gap-2 px-3 pb-3">
        <button
          onClick={onClose}
          data-testid="open-tables-check-cancel"
          className="h-13 py-3 rounded-lg bg-slate-700 border-2 border-slate-800 text-white font-bold hover:bg-slate-800 active:scale-[0.96] transition-all flex items-center justify-center gap-1.5 text-sm"
        >
          <XIcon size={16} />
          Άκυρο
        </button>
        <button
          onClick={() => press("clear")}
          data-testid="open-tables-check-clear"
          className="h-13 py-3 rounded-lg bg-red-600 border-2 border-red-700 text-white text-xl font-extrabold hover:bg-red-700 active:scale-[0.96] transition-all"
        >
          C
        </button>
        <button
          onClick={accept}
          disabled={!value}
          data-testid="open-tables-check-ok"
          className="h-13 py-3 rounded-lg bg-emerald-600 border-2 border-emerald-700 text-white font-bold hover:bg-emerald-700 active:scale-[0.96] transition-all flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Check size={18} />
          Έλεγχος
        </button>
      </div>
    </div>
  );
}
